if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback(){
      this.init();
    }

    init(){
      this.productTemplate = this.closest('product-template');
      this.quickAddModal = this.closest('quick-add-modal');
      this.productInfo = this.closest('product-info');
      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.form.addEventListener('change', this.onRelevantChangeHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      // Ready-to-ship configuration, shared by checkMtoMismatch() and updateSubmitButtonLabel().
      // Keys: selector name (lowercase). Values: the choice that gets 'rts' (lowercase).
      this.rtsOnlyChoices = {
        'jump ring finish': ['closed'],
        'select pattern # (see gallery)': ['original (unnumbered)', 'original', 'first edition (unnumbered)'],
        'select setting': ['as-is']
      };
      // Gates: if the selector has its choice but the customer picked a different one, nothing else is rts.
      this.rtsGates = { 'select setting': ['as-is'] };
      // Selectors (lowercase names) that keep their 'rts' classes but are ignored when deciding the
      // Add to bag / Pre-order button label (neither "is it complete" nor "is it ready to ship").
      this.buttonLabelExemptions = ['jump ring finish'];

      this.setOutOfStockOptions();
      this.checkMtoMismatch();
      this.setupConditionalCustomOptions();
      this.updateSubmitButtonLabel();
      // custom-option.js is deferred, so re-run once the component is defined/upgraded
      // in case anything above ran before the <custom-option> elements were ready.
      customElements.whenDefined('custom-option').then(() => this.refreshFormState());

      if (this.productTemplate.dataset.templateType == "quick-view") {
        this.setVariantImageChangeEvent();
      }
    }

    setOutOfStockOptions() {
      const variantSelector = this.querySelector('#js-variant-lookup');
      if(!variantSelector) return;

      const optionSelectors = this.querySelectorAll('.js-selector');
      if(optionSelectors?.length > 1) return; //more than 1 variant option, let the logic in product-template.js handle SOLD OUT labels

      const variantOptions = variantSelector?.querySelectorAll('option');

      for (const option of variantOptions) {
        const is_available = option.dataset.available;
        if(typeof is_available == 'undefined') continue;
        const option1 = option.dataset.option1
        const quantity = parseFloat(option.dataset.quantity);
        const policy = option.dataset.policy?.toLowerCase();
        const preorderUohOverride = option.dataset.preorderuohoverride === 'true';
        const is_mto = (quantity < 1 && policy === 'continue') || preorderUohOverride;

        const preorderUohOverrideLower = option.dataset.preorderuohoverride?.toLowerCase();
        const is_rts = quantity > 0 && (policy === 'deny' || (policy === 'continue' && preorderUohOverrideLower !== 'true'));

        if(is_available == 'false'){
          for (const optionSelector of optionSelectors) {
            if (optionSelector.classList.contains('js-adjacent-product')) {
              continue
            }
            for (const jsOption of optionSelector.querySelectorAll('.js-option')) {
              if(option1 == jsOption.dataset.value){
                jsOption.classList.add('unavailable') //show SOLD OUT message in variant dropdown
              }
            }
          }
        }

        if(is_mto){
          for (const optionSelector of optionSelectors) {
            if (optionSelector.classList.contains('js-adjacent-product')) {
              continue
            }
            for (const jsOption of optionSelector.querySelectorAll('.js-option')) {
              if(option1 == jsOption.dataset.value){
                jsOption.classList.add('mto') //show MADE TO ORDER message in variant dropdown
              }
            }
          }
        }

        if(is_rts){
          for (const optionSelector of optionSelectors) {
            if (optionSelector.classList.contains('js-adjacent-product')) {
              continue
            }
            for (const jsOption of optionSelector.querySelectorAll('.js-option')) {
              if(option1 == jsOption.dataset.value){
                jsOption.classList.add('rts') //show READY TO SHIP message in variant dropdown
              }
            }
          }
        }
      }
    }

    // Re-evaluates whether the customer's selected 'Metal Color' / 'Size' custom-option
    // picks match the currently selected variant's on-hand metal/size. If they don't
    // (i.e. the customer picked a combination that isn't actually in stock), the 'mto'
    // class is added to the selected radio's <label> so it can be styled as Made to Order.
    // Runs on init (initial state) and on any relevant 'change' event (variant dropdown,
    // or the Metal Color / Size custom-option radios).
    checkMtoMismatch() {
      const variantSelector = this.querySelector('#js-variant-lookup');
      if (!variantSelector) return;

      const selectedVariant = variantSelector.querySelector('option:checked');
      if (!selectedVariant) return;

      const variantDataset = selectedVariant.dataset;

      const customOptions = this.querySelectorAll('custom-option');
      if (!customOptions.length) return;

      // Is the currently selected variant itself MTO (mirrors the is_mto calculation in setOutOfStockOptions())?
      const variantQuantity = parseFloat(variantDataset.quantity);
      const variantPolicy = variantDataset.policy?.toLowerCase();
      const variantPreorderOverride = variantDataset.preorderuohoverride === 'true';
      const currentVariantIsMto = (variantQuantity < 1 && variantPolicy === 'continue') || variantPreorderOverride;

      // Is the currently selected variant ready to ship (mirrors the is_rts calculation in setOutOfStockOptions())?
      const variantPreorderOverrideLower = variantDataset.preorderuohoverride?.toLowerCase();
      const currentVariantIsRts = variantQuantity > 0 && (variantPolicy === 'deny' || (variantPolicy === 'continue' && variantPreorderOverrideLower !== 'true'));

      // Gates: a selector whose ready-to-ship choice controls whether ANY OTHER selector can be
      // ready to ship. If that choice exists but the customer has picked a different one (e.g.
      // "Reset in Platinum" instead of "As-Is"), nothing else on the form gets 'rts'.
      // Nothing picked yet = no suppression. Keys: selector name (lowercase). Values: gate choice(s).
      const rtsGates = this.rtsGates;
      let rtsSuppressed = false;
      customOptions.forEach((customOption) => {
        const names = this.getCustomOptionNames(customOption);
        const gateKey = Object.keys(rtsGates).find((key) => names.includes(key));
        if (!gateKey) return;
        const gateChoices = rtsGates[gateKey];
        const gateChoiceExists = Array.from(customOption.querySelectorAll('input[type="radio"]'))
          .some((radio) => this.matchesChoice(radio, gateChoices));
        const pickedRadio = customOption.getSelectedRadio ? customOption.getSelectedRadio() : null;
        if (gateChoiceExists && pickedRadio && !this.matchesChoice(pickedRadio, gateChoices)) {
          rtsSuppressed = true;
        }
      });

      customOptions.forEach((customOption) => {
        const propertyName = customOption.getPropertyName ? customOption.getPropertyName() : customOption.dataset.property;

        // Some custom options have no variant-level attribute to match against (they
        // aren't tracked like Metal Color/Size) - one specific choice is just inherently
        // the ready-to-ship one, so it only needs to be gated by the current variant's
        // own ready-to-ship status, not by a value match.
        // Keys: selector name (lowercase). Values: the choice that gets 'rts' (lowercase).
        const rtsOnlyChoices = this.rtsOnlyChoices;

        // The selector can be identified by its line item property name OR by its original legend text.
        const selectorNames = this.getCustomOptionNames(customOption);

        // The gate selector itself (e.g. Select Setting) is exempt from suppression; every other
        // selector loses 'rts' while the gate is closed (see rtsGates above).
        const isGateSelector = Object.keys(rtsGates).some((key) => selectorNames.includes(key));
        const suppressRts = rtsSuppressed && !isGateSelector;

        const rtsChoiceKey = Object.keys(rtsOnlyChoices).find((key) => selectorNames.includes(key));

        if (rtsChoiceKey) {
          const rtsChoices = rtsOnlyChoices[rtsChoiceKey];
          const radios = customOption.querySelectorAll('input[type="radio"]');
          radios.forEach((radio) => {
            const label = radio.closest('label');
            if (!label) return;

            label.classList.remove('rts');

            // match on the radio's value OR the label's visible text, case-insensitive
            const radioValue = radio.value?.trim().toLowerCase();
            const labelText = label.textContent?.trim().toLowerCase();
            const isRtsChoice = rtsChoices.includes(radioValue) || rtsChoices.includes(labelText);
            if (isRtsChoice && currentVariantIsRts && !suppressRts) {
              label.classList.add('rts');
            }
          });
          return;
        }

        // only Metal Color and Size have a corresponding variant-level value to compare against
        if (propertyName !== 'Metal Color' && propertyName !== 'Size') return;

        const radios = customOption.querySelectorAll('input[type="radio"]');

        radios.forEach((radio) => {
          const label = radio.closest('label');
          if (!label) return;

          // reset before recalculating
          label.classList.remove('mto');
          label.classList.remove('rts');

          const radioValue = radio.value?.trim();
          let matchesCurrentVariant = true;

          if (propertyName === 'Metal Color') {
            matchesCurrentVariant = (radioValue && radioValue === variantDataset.metalcolor) || radioValue === undefined;
          } else if (propertyName === 'Size') {
            matchesCurrentVariant = radioValue && radioValue === variantDataset.shanksize;
          }

          // flag mto if: this radio doesn't match the current variant at all (assumed unavailable),
          // OR it matches the current variant, but that variant itself is out of stock / MTO.
          const shouldFlagMto = !matchesCurrentVariant || (matchesCurrentVariant && currentVariantIsMto);

          if (shouldFlagMto) {
            label.classList.add('mto');
          }

          // flag rts only if this radio matches the current variant AND that variant is ready to ship.
          const shouldFlagRts = matchesCurrentVariant && currentVariantIsRts && !suppressRts;

          if (shouldFlagRts) {
            label.classList.add('rts');
          }
        });
      });
    }

    // Custom options that stay hidden until another selection contains a given string.
    // Key: the custom option's line item property name (its data-property).
    // Value: text (case-insensitive) that some other selection must contain to reveal it.
    setupConditionalCustomOptions() {
      this.conditionalCustomOptions = {
        'Metal Color': 'k gold'
      };

      this.updateConditionalCustomOptions();

      // Variant selectors (.js-selector) get data-selected updated by product-template.js on
      // every pick, so watching that attribute covers every way a selection can change.
      // (the callback runs after the click handler finishes, so product-template.js has already
      // finished rewriting the button text by the time we adjust it)
      const observer = new MutationObserver(() => this.refreshFormState());
      this.querySelectorAll('.js-selector').forEach((selector) => {
        observer.observe(selector, { attributes: true, attributeFilter: ['data-selected'] });
      });
      // (custom-option radio picks are handled in onRelevantChangeHandler)
    }

    updateConditionalCustomOptions() {
      const rules = this.conditionalCustomOptions || {};
      const customOptions = Array.from(this.querySelectorAll('custom-option'));

      // current selections from the real variant selectors (skip adjacent-product links)
      const variantSelectors = Array.from(this.querySelectorAll('.js-selector'))
        .filter((selector) => !selector.classList.contains('js-adjacent-product'));
      const variantSelections = variantSelectors.map((selector) => selector.dataset.selected || '');

      customOptions.forEach((customOption) => {
        const legend = customOption.querySelector('legend');
        // remember the original legend text so it can be restored when this gets hidden again
        if (customOption.dataset.originalLegend === undefined && legend) {
          customOption.dataset.originalLegend = legend.innerHTML;
        }

        const requiredText = rules[customOption.dataset.property];
        if (!requiredText) return;
        const needle = requiredText.toLowerCase();

        const otherCustomOptions = customOptions.filter((other) => other !== customOption);

        // Safety: if nothing on this page could ever contain the text (e.g. a product with no
        // metal selector), never hide it - otherwise a required choice would be unreachable.
        const possibleValues = [
          ...variantSelectors.flatMap((selector) =>
            Array.from(selector.querySelectorAll('.js-option')).map((option) => option.dataset.value || '')),
          ...otherCustomOptions.flatMap((other) =>
            Array.from(other.querySelectorAll('input[type="radio"]')).map((radio) => radio.value || ''))
        ];
        const canBeTriggered = possibleValues.some((value) => value.toLowerCase().includes(needle));
        if (!canBeTriggered) {
          this.setCustomOptionVisibility(customOption, true);
          return;
        }

        const otherSelections = otherCustomOptions.map((other) =>
          other.getSelectedValue ? (other.getSelectedValue() || '') : '');

        const shouldShow = [...variantSelections, ...otherSelections]
          .some((value) => value.toLowerCase().includes(needle));

        this.setCustomOptionVisibility(customOption, shouldShow);
      });
    }

    setCustomOptionVisibility(customOption, shouldShow) {
      if (shouldShow) {
        customOption.hidden = false;
        customOption.style.display = '';
        customOption.classList.add('is-revealed');
        return;
      }

      customOption.hidden = true;
      customOption.style.display = 'none';
      customOption.classList.remove('is-revealed', 'open');

      // Clear any earlier pick so a hidden option can never submit a stale value.
      customOption.querySelectorAll('input[type="radio"]').forEach((radio) => { radio.checked = false; });
      customOption.selectedRadio = null;
      const legend = customOption.querySelector('legend');
      if (legend && customOption.dataset.originalLegend !== undefined) {
        legend.innerHTML = customOption.dataset.originalLegend;
      }
    }

    // Names a custom option can be identified by (all lowercase): its line item property name and
    // its original legend text. Timing matters: on page load <custom-option> may not be upgraded
    // yet (its script is deferred), so .data doesn't exist. Fall back to the JSON tag (still in the
    // DOM until the component initializes), then to the live <legend> text (which is still the
    // original title before anything is picked).
    getCustomOptionNames(customOption) {
      const propertyName = customOption.getPropertyName ? customOption.getPropertyName() : customOption.dataset.property;

      let originalLegend = customOption.data?.default_select_state;
      if (!originalLegend) {
        try {
          const jsonTag = customOption.querySelector('script[type="application/json"]');
          originalLegend = jsonTag ? JSON.parse(jsonTag.textContent)?.default_select_state : null;
        } catch (e) {
          originalLegend = null;
        }
      }
      if (!originalLegend) originalLegend = customOption.dataset.originalLegend || customOption.querySelector('legend')?.textContent;

      return [propertyName, customOption.data?.property, originalLegend]
        .filter(Boolean)
        .map((name) => name.trim().toLowerCase());
    }

    // Does this radio's value OR its label's visible text match one of the choices (lowercase)?
    matchesChoice(radio, choices) {
      const value = radio.value?.trim().toLowerCase();
      const labelText = radio.closest('label')?.textContent?.trim().toLowerCase();
      return choices.includes(value) || choices.includes(labelText);
    }

    onRelevantChangeHandler(event) {
      const target = event.target;
      if (!target) return;

      const isVariantSelector = target.id === 'js-variant-lookup';
      const isCustomOptionRadio = target.type === 'radio' && target.closest('custom-option');

      if (isVariantSelector || isCustomOptionRadio) {
        // Wait a tick: custom-option.js stores selectedRadio just AFTER dispatching its change
        // event, and product-template.js rewrites the button text after dispatching the variant
        // change event - so read the final state once both have finished.
        setTimeout(() => this.refreshFormState(), 0);
      }
    }

    // Recompute everything that depends on the current selections, in dependency order.
    refreshFormState() {
      this.updateConditionalCustomOptions();
      this.checkMtoMismatch();
      this.updateSubmitButtonLabel();
    }

    // The custom options that count toward the button label (everything except the exemptions).
    getButtonLabelCustomOptions() {
      return Array.from(this.querySelectorAll('custom-option')).filter((customOption) => {
        const names = this.getCustomOptionNames(customOption);
        return !this.buttonLabelExemptions.some((exempt) => names.includes(exempt));
      });
    }

    // Every required selection has been made: all real variant selectors, plus every custom option
    // that is currently shown (hidden ones are intentionally not required).
    isProductFormComplete() {
      const variantSelectorsDone = Array.from(this.querySelectorAll('.js-selector'))
        .filter((selector) => !selector.classList.contains('js-adjacent-product'))
        .every((selector) => (selector.dataset.selected || '') !== '');

      const customOptionsDone = this.getButtonLabelCustomOptions()
        .filter((customOption) => !customOption.hidden)
        .every((customOption) => !!(customOption.getSelectedRadio && customOption.getSelectedRadio()));

      return variantSelectorsDone && customOptionsDone;
    }

    // Same ready-to-ship formula as is_rts in setOutOfStockOptions().
    isVariantReadyToShip(variantDataset) {
      const quantity = parseFloat(variantDataset.quantity);
      const policy = variantDataset.policy?.toLowerCase();
      const overrideLower = variantDataset.preorderuohoverride?.toLowerCase();
      return quantity > 0 && (policy === 'deny' || (policy === 'continue' && overrideLower !== 'true'));
    }

    // Ready to ship = the selected variant is ready to ship AND every custom option that has a
    // ready-to-ship rule (Metal Color, Size, and the rtsOnlyChoices selectors) currently has a
    // selected choice carrying the 'rts' class. Custom options with no rule (e.g. Jewelry Box)
    // don't affect it.
    isSelectionReadyToShip(variantDataset) {
      if (!this.isVariantReadyToShip(variantDataset)) return false;

      return this.getButtonLabelCustomOptions()
        .filter((customOption) => !customOption.hidden)
        .every((customOption) => {
          const propertyName = customOption.getPropertyName ? customOption.getPropertyName() : customOption.dataset.property;
          const names = this.getCustomOptionNames(customOption);
          const hasRtsRule = propertyName === 'Metal Color' || propertyName === 'Size'
            || Object.keys(this.rtsOnlyChoices).some((key) => names.includes(key));
          if (!hasRtsRule) return true;

          const pickedRadio = customOption.getSelectedRadio ? customOption.getSelectedRadio() : null;
          return !!pickedRadio && !!pickedRadio.closest('label')?.classList.contains('rts');
        });
    }

    // Add to bag button label: stays the default text until the form is complete; once complete,
    // it shows the pre-order text if the choices are NOT ready to ship. Never touches the
    // sold out / unavailable state, and skips variants without inventory tracking.
    updateSubmitButtonLabel() {
      const button = this.submitButton;
      const labelEl = button?.querySelector('[data-submit-button-text]') || button?.querySelector('span');
      const strings = window.variantStrings || {};
      if (!labelEl || !strings.addToCart || !strings.preOrder) return;
      if (button.disabled || button.hasAttribute('disabled')) {
        button.classList.remove('preOrder'); // sold out / unavailable is not a pre-order state
        return;
      }

      const selectedVariant = this.querySelector('#js-variant-lookup')?.querySelector('option:checked');
      const variantDataset = selectedVariant?.dataset;
      // untracked inventory has no quantity, so "ready to ship" can't be judged
      if (!variantDataset || !variantDataset.management) {
        button.classList.remove('preOrder');
        return;
      }

      // Products with no selectors at all (single-variant products) always keep the default label:
      // there is nothing for the customer to "complete", so we never switch them to pre-order.
      const hasSelectors =
        Array.from(this.querySelectorAll('.js-selector')).some((selector) => !selector.classList.contains('js-adjacent-product'))
        || this.getButtonLabelCustomOptions().length > 0;

      const showPreOrder = hasSelectors && this.isProductFormComplete() && !this.isSelectionReadyToShip(variantDataset);
      const text = showPreOrder ? strings.preOrder : strings.addToCart;
      if (labelEl.innerText.trim() !== text.trim()) labelEl.innerText = text;

      // 'Button Button__primary' <-> 'Button Button__primary preOrder', in step with the label
      button.classList.toggle('preOrder', showPreOrder);
    }

    setVariantImageChangeEvent() {
      let variantSelectors = this.querySelectorAll('.Option__wrapper-visual');
      let hiddenVariantSelector = this.querySelector('#js-variant-lookup');
      for (const selector of variantSelectors) {
        let optionSelectors = selector.querySelectorAll(".js-option");
        for (const option of optionSelectors) {
          option.addEventListener('click', () => {
            let variantSelector = hiddenVariantSelector.querySelector(`[data-option${option.dataset.optionIndex}='${option.dataset.value}']`);
            document.getElementById("variant_image_container").querySelector("img").setAttribute("src", variantSelector.dataset.imageSrc);
          });
        }
      }
    }

    displayErrors(errors) {
      const productErrors = this.querySelector('.Product__errors')
      productErrors.innerText = ''
      if (errors.length > 1) {
        for (const error of errors) {
          productErrors.insertAdjacentHTML('beforeend', `${error}<br>`)
        }
      } else {
        productErrors.insertAdjacentHTML('beforeend', `${errors[0]}`)
      }

      productErrors.classList.add('shown')
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      // on an empty run like this, error messaging will be cleared.
      this.handleErrorMessage();

      // extra product errors, possibly taken from previous Mociun code.
      const productErrors = document.querySelector('.Product__errors');
      productErrors.innerText = '';

      // add loading class to the submit button.
      this.submitButton.classList.add('loading');

      // initialize cartItems array. Items in this array will be added to cart at the end of evaluation.
      const cartItems = [];

      // select element containing the master variant list
      const variantSelector = this.querySelector('#js-variant-lookup');

      // grab the selected option inside of the variantSelector
      const selectedVariant = variantSelector.querySelector('option:checked');

      // the dataset of the selectedVariant
      const variantDataset = selectedVariant.dataset

      // initial variant data to send to cart/add.js
      const mainProduct = {
        id: parseInt(selectedVariant.value, 10),
        quantity: 1, // default to 1.
      }

      // quantity input for the product
      const quantityInput = document.querySelector('#Quantity');

      // variant quantity to send to cart
      if (quantityInput) {
        const quantityInputValue = parseInt(quantityInput.value, 10);
        if(quantityInputValue > 0){
          mainProduct.quantity = quantityInputValue;
        }
      }

      // Initialize property object for line item
      mainProduct.properties = {};

      // variant inventory properties
      if(variantDataset.policy == 'deny'){
        mainProduct.properties['_inventory_policy'] = variantDataset.policy
        mainProduct.properties['_inventory_quantity'] = variantDataset.quantity
      }

      // custom option sets
      const customOptions = this.querySelectorAll('custom-option');

      // build a list of variants to ATC if the custom option selected does so.
      if(customOptions.length > 0){
        customOptions.forEach((customOption)=>{
          const propertyName = customOption.getPropertyName();
          const selectedRadio = customOption.getSelectedRadio();
          if(selectedRadio){
            mainProduct.properties[propertyName] = selectedRadio.value;
          }
        });
      }

      // populate base size for resizable rings/bands
      // if(variantDataset.shanksize != ''){
      //   mainProduct.properties['Base Size'] = `${variantDataset.shanksize}`;
      // }
      
      // order notes property
      // const orderNotes = this.querySelector('input[name="properties[Order notes]"]');
      // if (orderNotes && orderNotes.value != ''){
        // mainProduct.properties['Order note'] = orderNotes.value
      // }


      // const selectedMetalOnHand =
      //   !mainProduct.properties['Metal Color'];

      // const selectedMetalOnHand =
      //   mainProduct.properties['Metal Color'] &&
      //   mainProduct.properties['Metal Color'].trim() === variantDataset.metalcolor;

      // Determine if 'Metal Color' is the metal setting option OR if 'Metal Color' is not set at all
      const metalColorValue = mainProduct.properties['Metal Color'];
      const selectedMetalOnHand =
        (metalColorValue && metalColorValue.trim() === variantDataset.metalcolor) || metalColorValue === undefined;

      const selectedSizeOnHand =
        mainProduct.properties['Size'] &&
        mainProduct.properties['Size'].trim() === variantDataset.shanksize;
      
      const winterBreak = variantDataset.winterbreak;   
      const immediateShipText = winterBreak !== '' ? "This piece is in stock and will ship immediately after Mociun's winter break." : "This piece is in stock and ships immediately.";
      const immediateShipFinalSaleText = winterBreak !== '' ? "This final sale piece is in stock and will ship immediately after Mociun's winter break." : "This final sale piece is in stock and ships immediately."; 

      const orderNotes = this.querySelector('input[name="properties[Order notes]"]');
      const cartmessageProduct = this.querySelector('input[name="properties[FYI]"]');
      const mtoInventory = this.querySelector('input[name*="inventory"]');
      const inventoryType = mtoInventory?.value;
      const noteIsBlank = !orderNotes || orderNotes.value.trim().length === 0;
    
      const preorderOverride = variantDataset.preorderuohoverride == true || variantDataset.preorderuohoverride === 'true';
      let processedOrderNote = orderNotes?.value || '';
      if (processedOrderNote) {
        const lowerNote = processedOrderNote.toLowerCase();
        const containsFinalSale = lowerNote.includes('final sale');
        const containsCreateAndShip = lowerNote.includes('create and ship.');
        if (processedOrderNote) {
          const lowerNote = processedOrderNote.toLowerCase();
          if (lowerNote.includes('create and ship') && !lowerNote.includes('final sale')) {
            processedOrderNote = "This piece is final sale. " + processedOrderNote;
          }
        }
      }      

      const baseImmediateShip = variantDataset.quantity > 0 && selectedMetalOnHand;
      const ringImmediateShip = baseImmediateShip && selectedSizeOnHand;

      const immediateShipNoteMap = {
      '_mtojewelryfinal': immediateShipFinalSaleText,
      '_mtojewelry': immediateShipText
    };
      if (noteIsBlank) {
        console.log(winterBreak);
        if (inventoryType === '_mtoringsbands' && ringImmediateShip) {
          mainProduct.properties['Note'] = immediateShipText;
        }
        else if (immediateShipNoteMap[inventoryType] && baseImmediateShip) {
          mainProduct.properties['Note'] = immediateShipNoteMap[inventoryType];
        }
      }
      else {
        console.log(winterBreak);
        if (inventoryType === '_mtoringsbands' && ringImmediateShip) {
          mainProduct.properties['Note'] = immediateShipText;
        }
        else if (immediateShipNoteMap[inventoryType] && baseImmediateShip) {
          mainProduct.properties['Note'] = immediateShipNoteMap[inventoryType];
        }
        else {
          mainProduct.properties['Note'] = processedOrderNote;
        }
    }

      //if (orderNotes && orderNotes.value.trim().length > 0) {
        //if (mtoInventory && mtoInventory.value == '_mtojewelryfinal' && variantDataset.quantity > 0 && selectedMetalOnHand) {
            // //variant is MTO and on hand
            //mainProduct.properties['Note'] = immediateShipFinalSaleText;
          //}
            //else if (mtoInventory && mtoInventory.value == '_mtojewelry' && variantDataset.quantity > 0 && selectedMetalOnHand) {
              //mainProduct.properties['Note'] = immediateShipText;
            //}
            //else if (mtoInventory && mtoInventory.value == '_mtoringsbands' && variantDataset.quantity > 0 && selectedMetalOnHand && selectedSizeOnHand) {
              //mainProduct.properties['Note'] = immediateShipText;
            //}
            //else {
             //mainProduct.properties['Note'] = orderNotes.value;
           //}
        //}
      
      // populate a (secondary to order notes) product-down message as line item cart property
      if(cartmessageProduct && cartmessageProduct.value.trim().length > 0){
        mainProduct.properties['FYI'] = cartmessageProduct.value;
      }
      // populate a variant-down message as line item cart property
      if(variantDataset.cartmessageprimaryvariant != ''){
        mainProduct.properties['Extras'] = `${variantDataset.cartmessageprimaryvariant}`;
      }

      // category notes property
      const giftsByMociunNotes = this.querySelector('input[name="properties[Gifts by Mociun]"]');
      if (giftsByMociunNotes && giftsByMociunNotes.value.trim().length > 0){
        mainProduct.properties['Gifts by Mociun'] = giftsByMociunNotes.value
      }


      // Explore the product template component for any input element with name attribute starting with "properties["
      if (this.productTemplate) {
        const inputProperties = this.productTemplate.querySelectorAll('input[name^="properties["]');
        const skipProperties = ["Gifts by Mociun", "Order notes"];  // these are properties we have already evaluated above
        // hidden custom options (see updateConditionalCustomOptions) must not submit anything,
        // otherwise this loop would pick up the first radio's value even though nothing is selected
        this.querySelectorAll('custom-option').forEach((hiddenOption) => {
          if (hiddenOption.hidden && hiddenOption.dataset.property) {
            skipProperties.push(hiddenOption.dataset.property);
          }
        });
        if (inputProperties.length > 0) {
          for (const inputProperty of inputProperties) {
            // Extract the potential property name from the input.
            const propertyNameMatch = inputProperty.name.match(/properties\[\s*['"]?(.+?)['"]?\s*\]/);
            if (propertyNameMatch && propertyNameMatch[1]) {
              const propertyTitle = propertyNameMatch[1];
              // If the property name is in our "skip list" or already exists in mainProduct.properties, move on.
              if (skipProperties.includes(propertyTitle) || mainProduct.properties.hasOwnProperty(propertyTitle)) {
                continue;
              }
              const propertyValue = inputProperty.value;
              if (propertyValue.length > 0) {
                mainProduct.properties[propertyTitle] = propertyValue;
              }
            }
          }
        }
      }

      // Mociun gemstone selection.
      // in the event a product does not offer gemstones, initialize the flag to true.
      let allGemsSelected = true;

      // collect the gemstone selectors
      const gemstoneSelectors = this.querySelectorAll('.js-gemstone');

      // atcGemstones array will hold the ATC data of the selected gemstones.
      const atcGemstones = [];

      // if we have gemstoneSelectors, go through each one and add the gemstone to the atcGemstones list. If a value cannot be determined, break the loop and flag allGemsSelected to false.
      if(gemstoneSelectors.length > 0){

        mainProduct.properties._price = parseInt(variantDataset.price, 10);

        for (const gemstoneSelector of gemstoneSelectors) {
          // collect the property name and value of the gemstone
          const name = gemstoneSelector.dataset.option;
          const value = gemstoneSelector.dataset.selected;

          if (value === '') {
            allGemsSelected = false;
            break; // at this point we would enter the error state later down the road. break out of this loop.
          }

          // add birthstone property to the main product
          mainProduct.properties[name] = value;

          // Associated variants in case of remove
          mainProduct.properties._variants = mainProduct.properties._variants ? `${mainProduct.properties._variants}, ${gemstoneSelector.dataset.id}` : `${gemstoneSelector.dataset.id}`;

          // update the price property to account for the gemstone price.
          mainProduct.properties._price += parseInt(gemstoneSelector.dataset.price, 10);
        }

        // in the event all gemstones are selected, add these items to our list.
        if(allGemsSelected){
          // no errors, add the gemstones to our atcGemstones list
          for (const gemstoneSelector of gemstoneSelectors) {
            // grab the id of the gemstone variant we want to add to cart.
            const id = parseInt(gemstoneSelector.dataset.id, 10);

            // Find existing gemstone object with the same id
            const existingGemstone = atcGemstones.find(gemstone => gemstone.id === id);

            if (existingGemstone) {
              // If the gemstone already exists in the array, increase the quantity
              existingGemstone.quantity += 1;
              existingGemstone.properties._originalQuantity = existingGemstone.quantity;
            } else {
              // If the gemstone does not exist in the array, add a new object
              atcGemstones.push({
                id: id,
                quantity: 1,
                properties: {
                  "For": `${this.productInfo?.dataset.title}`,
                  _cartHide: true,
                  _quantityHide: true
                }
              });
            }
          }
        }
      }

      // Error handling
      const errors = [];

      // quantity less than 1 error flag
      if (mainProduct.quantity < 1) {
        errors.push('Please select a quantity');
      }

      // invalid variant id error flag
      if (isNaN(mainProduct.id)) {
        let mainVariantsSelected = true;
        const titles = document.querySelectorAll('.Option__wrapper-visual-title');
        for (const title of titles) {
          if (title.innerText.includes('SELECT ')) {
            const label = title.innerText.split('SELECT ')[1];
            errors.push(`Please select a ${label.toLowerCase()}`);
            mainVariantsSelected = false;
          }
        }

        if (isNaN(mainProduct.id) && mainVariantsSelected) {
          errors.push('This product is currently unavailable.');
        }
      }

      // incomplete gemstones error flag
      if (!allGemsSelected) {
        errors.push('Please select a birthstone for each setting!');
      }

      // build a list of variants to ATC if the custom option selected does so.
      if(customOptions.length > 0){
        customOptions.forEach((customOption)=>{
          const propertyName = customOption.getPropertyName();
          const selectedRadio = customOption.getSelectedRadio();
          if(!selectedRadio && !customOption.hidden){ // hidden options are intentionally not required
            errors.push(`Please select a ${propertyName.toLowerCase()}`);
          }
        });
      }

      if (errors.length > 0){
        this.displayErrors(errors);
        return false; // exit this function
      }
      if (errors.length <=  0){
        let addBtn = document.querySelector(".add_to_cart");
        if(addBtn){
          addBtn.classList.remove('bg-color');
        } 
      }

      /**
       * At this stage we are ready for ATC.
       * Update the cartItems array with our main product, and then look for any gemstones or complimentary boxes to add.
       */

      let productGroup = false;
      const productGroupId = `${this.productInfo?.dataset.id}:${Date.now()}`;
      // push the complimentary items related to the main product.
      // pair with script https://admin.shopify.com/store/mociun-com/apps/script-editor/scripts/246218888
      const complimentaryBoxInput = this.querySelector('input[name="complimentary_box"]');
      const complimentaryEvaluationInput = this.querySelector('input[name="complimentary_evaluation"]');
      const complimentaryCleanerInput = this.querySelector('input[name="complimentary_cleaner"]');
      const complimentaryCertificationInput = this.querySelector('input[name="complimentary_certification"]');
      const complimentaryWrapInput = this.querySelector('input[name="complimentary_wrap"]');

      const customOptionsWithVariants = [];
      if(customOptions.length > 0){
        customOptions.forEach((customOption)=>{
          const customOptionData = customOption.getSelectedData();
          if(!(customOptionData && customOptionData.variants)) return false;
          customOptionsWithVariants.push(customOptionData);
        });
      }

      if(complimentaryBoxInput || complimentaryCleanerInput || complimentaryEvaluationInput || complimentaryCertificationInput || complimentaryWrapInput || atcGemstones.length > 0 || customOptionsWithVariants.length > 0){
        productGroup = true;
      }

      // push the main product.
      if(productGroup){
        mainProduct.properties._productGroup = productGroupId;
        mainProduct.properties._productGroupMain = true;
      }

      cartItems.push(mainProduct);

      // push gemstones related to the main product.
      if(atcGemstones.length > 0){
        for (const atcGemstone of atcGemstones) {
          atcGemstone.properties._productGroup = productGroupId;
          cartItems.push(atcGemstone);
        }
      }

      // evaluate and add products to cart that are included via custom product options.
      if(customOptionsWithVariants.length > 0){
        for (const customOptionWithVariants of customOptionsWithVariants) {
          const selectedTitle = customOptionWithVariants.title;
          const selectedProperty = customOptionWithVariants.property;
          const selectedValue = customOptionWithVariants.value;
          if(customOptionWithVariants.variants.length > 0){
            for (const variant of customOptionWithVariants.variants) {
              const productAddon = {
                id: variant.id,
                quantity: (variant.quantity * mainProduct.quantity), // addons may have a default quantity, keep it in sync with the main product.
                properties: {
                  _productGroup: productGroupId,
                  _originalQuantity: (variant.quantity * mainProduct.quantity),
                  _mainProductPropertyKey: `${selectedProperty}`,
                  _mainProductPropertyValue: `${selectedValue}`,
                  _quantityHide: true
                }
              };
              if (selectedTitle.includes("+$") && !selectedTitle.includes("+$0")) {
                productAddon.properties["For"] = `${this.productInfo?.dataset.title}`;
              } else {
                productAddon.properties["Complimentary with"] = `${this.productInfo?.dataset.title}`;
              }
              if(variant.cart_hide){
                productAddon.properties._cartHide = true;
              }
              if(variant.fallback){
                productAddon.properties._fallbackVariantId = variant.fallback.id;
                productAddon.properties._fallbackQuantity = variant.fallback.quantity;
                productAddon.properties._fallbackCartHide = variant.fallback.cart_hide;
                productAddon.properties._fallbackPropertyTitle = `${selectedProperty}`;
                productAddon.properties._fallbackPropertyValue = `${variant.fallback.title}`;
              }
              cartItems.push(productAddon);
            }
          }
        }
      }

      if(complimentaryBoxInput){
        const complimentaryBox = {
          id: parseInt(complimentaryBoxInput.value, 10),
          quantity: mainProduct.quantity, // keep the quantity of the complimentary box in sync with the main product.
          properties: {
            "Complimentary with": `${this.productInfo?.dataset.title}`,
            _productGroup: productGroupId,
            _originalQuantity: mainProduct.quantity,
            _quantityHide: true
          }
        };
        if(complimentaryBoxInput.dataset.cartHide == "true"){
          complimentaryBox.properties._cartHide = true;
        }
        cartItems.push(complimentaryBox);
      }

      if(complimentaryCleanerInput){
        productGroup = true;
        const complimentaryCleaner = {
          id: parseInt(complimentaryCleanerInput.value, 10),
          quantity: mainProduct.quantity, // keep the quantity of the complimentary cleaner in sync with the main product.
          properties: {
            "Complimentary with": `${this.productInfo?.dataset.title}`,
            _productGroup: productGroupId,
            _originalQuantity: mainProduct.quantity,
            _quantityHide: true
          }
        };
        if(complimentaryCleanerInput.dataset.cartHide == "true"){
          complimentaryCleaner.properties._cartHide = true;
        }
        cartItems.push(complimentaryCleaner);
      }

      if(complimentaryEvaluationInput){
        productGroup = true;
        const complimentaryEvaluation = {
          id: parseInt(complimentaryEvaluationInput.value, 10),
          quantity: mainProduct.quantity, // keep the quantity of the complimentary evaluation in sync with the main product.
          properties: {
            "Complimentary with": `${this.productInfo?.dataset.title}`,
            _productGroup: productGroupId,
            _originalQuantity: mainProduct.quantity,
            _quantityHide: true
          }
        };
        if(complimentaryEvaluationInput.dataset.cartHide == "true"){
          complimentaryEvaluation.properties._cartHide = true;
        }
        cartItems.push(complimentaryEvaluation);
      }

      if(complimentaryCertificationInput){
        productGroup = true;
        const complimentaryCertification = {
          id: parseInt(complimentaryCertificationInput.value, 10),
          quantity: mainProduct.quantity, // keep the quantity of the complimentary GIA certification in sync with the main product.
          properties: {
            "Complimentary with": `${this.productInfo?.dataset.title}`,
            _productGroup: productGroupId,
            _originalQuantity: mainProduct.quantity,
            _quantityHide: true
          }
        };
        if(complimentaryCertificationInput.dataset.cartHide == "true"){
          complimentaryCertification.properties._cartHide = true;
        }
        cartItems.push(complimentaryCertification);
      }

      if(complimentaryWrapInput){
        productGroup = true;
        const complimentaryWrap = {
          id: parseInt(complimentaryWrapInput.value, 10),
          quantity: mainProduct.quantity, // keep the quantity of the complimentary GIA certification in sync with the main product.
          properties: {
            "Complimentary with": `${this.productInfo?.dataset.title}`,
            _productGroup: productGroupId,
            _originalQuantity: mainProduct.quantity,
            _quantityHide: true
          }
        };
        if(complimentaryWrapInput.dataset.cartHide == "true"){
          complimentaryWrap.properties._cartHide = true;
        }
        cartItems.push(complimentaryWrap);
      }

      cartItems.reverse();

      /**
       * Add Items To Cart
       * First check inventory policy. Do we continue to sell even under 0 stock? Add the items to cart.
       * Do we want to only sell in stock items? Pull the cart.
       * If our main item is not already in the cart, add the cartItems.
       * If our main item is already in the cart, compare the amount in the cart, how many more we want to add, and how many are left at all. If we can add more to the cart, proceed.
       */

      // console.log(cartItems);
      // console.log(`variantDataset.policy: ${variantDataset.policy}`);

      if (variantDataset.policy === 'continue') {
        await window.theme.addToCart(
          cartItems
        )
        .then((result) => {
          if (result.error) {
            console.log(result.error);
            const errors = [];
            errors.push(result.error);
            this.displayErrors(errors);
            return
          }
          this.error = false;
          if (this.quickAddModal) {
            this.quickAddModal.hide(true);
          }
          window.theme.fetchAndOpenCart();
          const errorDiv = document.querySelector(".Product__errors");
          const closeBtn = document.querySelector(".close-quick-drawer");
          
          if (errorDiv) {
            const errorMessage = errorDiv.textContent.toLowerCase().trim();
            const specificText = "only 1 are available";
            
            if (!errorMessage.includes(specificText)) {
              if (closeBtn) {
                closeBtn.click();
              }
            }
          }
        }).finally(() => {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
        });
      }else{
        const errors= [];
        const available = variantDataset.quantity;
        let count = 0;
        let currentCount = 0;
        let requestTotal = mainProduct.quantity;
        const cart = await window.theme.fetchCart();

        let items = JSON.parse(JSON.stringify(cart));

        const itemInCart = items.filter(item => item.id == mainProduct.id);
        if(itemInCart.length > 0){
          for (const item of itemInCart) {
            count += item.quantity;
            currentCount += item.quantity;
          }
          count += requestTotal;
          if (count > available) {
            if (currentCount === 0) {
              errors.push(`Cannot add to your cart: only ${available} are available.`);
            } else {
              errors.push(`Cannot add to your cart: only ${available} are available, and ${currentCount} are already in your cart.`);
            }
            this.displayErrors(errors);
          } else if (count === available) {
            errors.push('You added the last available item of this kind to your cart!');
            this.displayErrors(errors);
          } else if (count < available) {
            await window.theme.addToCart(
              cartItems
            )
            if (this.quickAddModal) {
              this.quickAddModal.hide(true);
            }
            window.theme.fetchAndOpenCart();
          }
        }else {
          await window.theme.addToCart(
            cartItems
          )
          if (this.quickAddModal) {
            this.quickAddModal.hide(true);
          }
          window.theme.fetchAndOpenCart();
        }
        const errorDiv = document.querySelector(".Product__errors");
        const closeBtn = document.querySelector(".close-quick-drawer");
        
        if (errorDiv) {
          const errorMessage = errorDiv.textContent.toLowerCase().trim();
          const specificText = "only 1 are available";
          
          if (!errorMessage.includes(specificText)) {
            if (closeBtn) {
              closeBtn.click();
            }
          }
        }
        
        
      }
      
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });



}